import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { OrdinalRatingEnum } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { LineupBonuses } from '@sorare/core/src/atoms/icons/LineupBonuses';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { messages } from 'components/managerTeam/Status/messages';
import { ContenderStatus, getContenderStatus } from 'lib/contender';

import { ContenderBonus } from '../ContenderBonus';
import type { ContenderUpcomingFooter_so5LeaderboardContender } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ center: true, gap: 0 })`
  height: 100%;
  min-height: calc(5 * var(--unit));

  & > :last-child:not(:only-child) {
    border-left: 1px solid var(--c-nd-150);
  }
  & > * {
    flex: 1;
  }
`;

type Props = {
  so5LeaderboardContender: ContenderUpcomingFooter_so5LeaderboardContender;
  onClickPrizePool?: () => void;
  onClickBonus?: () => void;
};
export const ContenderUpcomingFooter = ({
  so5LeaderboardContender,
  onClickPrizePool,
  onClickBonus,
}: Props) => {
  const { so5Lineup } = so5LeaderboardContender;
  const { bonuses = [] } = so5Lineup || {};
  const activeBonus =
    bonuses.reduce((sum, { bonusBasisPoints }) => sum + bonusBasisPoints, 0) /
    10000;

  const contenderStatus = getContenderStatus(so5LeaderboardContender);

  let icon: ReactNode;
  let text: ReactNode;
  let onClick = onClickPrizePool;
  switch (contenderStatus.status) {
    case ContenderStatus.EMPTY:
      icon = <Box size={2} color="#FF4E22" />;
      text = <FormattedMessage {...fantasy.viewPrizePool} />;
      break;
    case ContenderStatus.INCOMPLETE:
      icon = (
        <FontAwesomeIcon
          icon={faExclamationCircle}
          color="var(--c-yellow-400)"
          fontSize={16}
        />
      );
      text = <FormattedMessage {...fantasy.incomplete} />;
      break;
    case ContenderStatus.PROJECTED_DIVISION:
      icon = <DumbStatus divisionChange={contenderStatus.divisionChange} />;
      text = (
        <FormattedMessage
          {...messages.projectedToDiv}
          values={{ division: contenderStatus.projectedDivision }}
        />
      );
      break;
    default:
      icon = (
        <ContenderBonus
          bonusRating={so5Lineup?.bonusRating || OrdinalRatingEnum.MEDIUM}
        />
      );
      text = <FormattedMessage {...fantasy.levelUp} />;
      onClick = onClickBonus;
      break;
  }

  return (
    <ConditionalWrapper
      wrap={!!onClick}
      Wrapper={ButtonBase}
      props={{ onClick }}
    >
      <Root>
        {bonuses.length > 0 && (
          <Horizontal gap={0.5} center>
            <LineupBonuses />
            <BrandS>
              <FormattedNumber
                value={activeBonus}
                signDisplay="always"
                style="percent"
              />
            </BrandS>
          </Horizontal>
        )}
        <Horizontal gap={0.5} center>
          {icon}
          <BrandS>{text}</BrandS>
        </Horizontal>
      </Root>
    </ConditionalWrapper>
  );
};

ContenderUpcomingFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderUpcomingFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        bonusRating
        bonuses {
          bonusBasisPoints
        }
      }
      ...getContenderStatus_so5LeaderboardContender
    }
    ${getContenderStatus.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<ContenderUpcomingFooter_so5LeaderboardContender>,
};
